import type { Case, CaseState, RecommendedAction } from '@/versions/v1/schema/case';
import type { IssueSeverity, PlantCaseKind, PriorityUrgency, RoutedRole } from '@/versions/v1/schema/advisory-issue';
import type {
  KpiTile,
  PlantRole,
  PlantRoleId,
  RoleAlert,
  RoleTable,
} from '@/versions/v1/schema/plant-role';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { Persona } from '@/shared/types';
import { applyCases } from '@/versions/v1/shared/ScenarioEngine';
import {
  CASES,
  FMEA_UPDATE_REVIEWS,
  RECOMMENDED_ACTIONS,
} from '@/versions/v1/shared/bunker-hill-advisory-seed';
import {
  CONTROL_LOOPS,
  EXTRA_ROLE_TABLES,
  PLANT_ROLES,
  ROLE_ALERTS,
  ROLE_KPIS,
  ROLE_TABLES,
  ROLE_TABS,
} from '@/versions/v1/shared/bunker-hill-seed';
import { plantPhrase } from '@/versions/v1/shared/project-seed';

export type OverviewCopy = {
  title: string;
  description: string;
};

export type RoleDashboard = {
  role: PlantRole;
  kpis: KpiTile[];
  tables: RoleTable[];
  alerts: RoleAlert[];
  showAdvisory: boolean;
  showCases: boolean;
  showAlarmPanel: boolean;
};

const ALL_TABLES: readonly RoleTable[] = [...ROLE_TABLES, ...EXTRA_ROLE_TABLES];

const SEVERITY_RANK: Record<IssueSeverity, number> = {
  high: 0,
  med: 1,
  low: 2,
  monitor: 3,
};

const OVERVIEW_FINDING_IDS = [
  'case-zn-recovery',
  'case-ag-grade',
  'case-p80',
  'case-as-trend',
] as const;

const OVERVIEW_WORKED_EXAMPLE_IDS = [
  'case-air',
  'case-cuso4',
  'case-tramp',
  'case-lic',
  'case-gangue',
] as const;

export const SEVERITY_LABEL: Record<IssueSeverity, string> = {
  high: 'High',
  med: 'Med',
  low: 'Low',
  monitor: 'Monitor',
};

export const ROUTED_ROLE_LABEL: Record<RoutedRole, string> = {
  operator: 'Operator',
  metallurgist: 'Metallurgist',
  maintenance: 'Maintenance',
  superintendent: 'Superintendent',
};

export const URGENCY_LABEL: Record<PriorityUrgency, string> = {
  now: 'Now',
  today: 'Today',
  week: 'This week',
};

export const CASE_KIND_LABEL: Record<PlantCaseKind, string> = {
  'zero-touch': 'zero-touch · no diagnosis',
  'lab-sample': 'Lab sample',
  visual: 'Visual inspect',
  'control-mode': 'Control mode',
  'new-pattern': 'New pattern',
  hardness: 'Hardness',
};

export const CASE_STATE_LABEL: Record<CaseState, string> = {
  detected: 'Detected',
  matched: 'Matched',
  routed: 'Routed',
  'evidence-requested': 'Evidence requested',
  escalated: 'Escalated',
  diagnosed: 'Diagnosed',
  'outcome-documented': 'Outcome documented',
  'fmea-review': 'FMEA review',
  approved: 'Approved',
  rejected: 'Rejected',
  closed: 'Closed',
};

const PERSONA_TO_ROLE: Partial<Record<Persona, PlantRoleId>> = {
  GM: 'gm',
  Superintendent: 'superintendent',
  Metallurgist: 'metallurgist',
  'Senior metallurgist': 'senior-metallurgist',
  'Ops supervisor': 'ops-supervisor',
  Maintenance: 'maintenance',
  'Control room': 'cro',
  Operator: 'operator',
};

const ROLE_TO_PERSONA: Record<PlantRoleId, Persona> = {
  gm: 'GM',
  superintendent: 'Superintendent',
  metallurgist: 'Metallurgist',
  'senior-metallurgist': 'Senior metallurgist',
  'ops-supervisor': 'Ops supervisor',
  maintenance: 'Maintenance',
  cro: 'Control room',
  operator: 'Operator',
};

export function overviewCopy(plantName: string): OverviewCopy {
  return {
    title: 'Overview',
    description: `Persona-resolved landing for ${plantPhrase(plantName)} — the seven deck main pages. Deeper surfaces live on their module routes.`,
  };
}

export function plantRoleTabs(): { id: PlantRoleId; label: string }[] {
  return PLANT_ROLES.map((role) => ({ id: role.id, label: role.label }));
}

export function defaultTabForRole(roleId: PlantRoleId): string {
  const tabs = ROLE_TABS.filter((tab) => tab.roleId === roleId);
  return tabs[0]?.id ?? 'overview';
}

function tablesForRoleOverview(roleId: PlantRoleId, tabId: string): RoleTable[] {
  return ALL_TABLES.filter((table) => {
    if (table.roleId !== roleId || table.tabId !== tabId) return false;
    if (roleId === 'maintenance') {
      return table.caption.startsWith('Open work orders');
    }
    return true;
  });
}

export function dashboardForRole(roleId: PlantRoleId): RoleDashboard {
  const role = PLANT_ROLES.find((item) => item.id === roleId) ?? PLANT_ROLES[0];
  const tabId = defaultTabForRole(role.id);
  return {
    role,
    kpis: ROLE_KPIS.filter((kpi) => {
      if (kpi.roleId !== role.id) return false;
      if (kpi.tabId) return kpi.tabId === tabId;
      return true;
    }),
    tables: tablesForRoleOverview(role.id, tabId),
    alerts: ROLE_ALERTS.filter((alert) => alert.roleId === role.id),
    showAdvisory: role.id === 'superintendent' || role.id === 'metallurgist',
    showCases: false, // §6 worked examples live in the Advisory queue (F3); the role landing shows findings only
    showAlarmPanel:
      role.id === 'gm' || role.id === 'cro' || role.id === 'ops-supervisor',
  };
}

export function defaultPlantRole(): PlantRoleId {
  return 'gm';
}

export function plantRoleIdFromPersona(persona: Persona): PlantRoleId | null {
  return PERSONA_TO_ROLE[persona] ?? null;
}

export function personaFromPlantRole(roleId: PlantRoleId): Persona {
  return ROLE_TO_PERSONA[roleId];
}

export function parseRoleSearchParam(value: string | null): PlantRoleId | null {
  if (!value) return null;
  return PLANT_ROLES.some((role) => role.id === value) ? (value as PlantRoleId) : null;
}

export function pendingFmeaReviewCount(): number {
  return FMEA_UPDATE_REVIEWS.filter((row) => row.reviewStatus === 'pending').length;
}

/** Deck AI Performance findings (+ hardness when scenario active). */
export function overviewFindings(state: SessionState): Case[] {
  const cases = applyCases(CASES, state);
  const ids = new Set<string>(OVERVIEW_FINDING_IDS);
  const computed = cases.filter((row) => row.kind === 'hardness').map((row) => row.id);
  for (const id of computed) ids.add(id);
  return sortCases(cases.filter((row) => ids.has(row.id)));
}

/** Word-doc §6 worked examples for the Super / Met cases table. */
export function overviewWorkedCases(state: SessionState): Case[] {
  const cases = applyCases(CASES, state);
  const ids = new Set<string>(OVERVIEW_WORKED_EXAMPLE_IDS);
  return cases.filter((row) => ids.has(row.id));
}

export function overviewRecommendedActions(): readonly RecommendedAction[] {
  return RECOMMENDED_ACTIONS.slice().filter((a) => a.sequence !== null).sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
}

export function sortCases(cases: readonly Case[]): Case[] {
  return cases.slice().sort((a, b) => {
    const rank = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    if (rank !== 0) return rank;
    return a.raisedAt.localeCompare(b.raisedAt);
  });
}

export function bunkerHillLoops(): readonly typeof CONTROL_LOOPS[number][] {
  return CONTROL_LOOPS;
}
